import {
  Card,
  Empty,
  message,
  Segmented,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useRef, useState } from 'react';

import ParentLink from '../ClubRegistration/ParentLink';
import type { ParentDetails } from '../ClubRegistration/types';
import { formatMNT, queryErrorMessage } from '../ClubRegistration/types';

const { Text } = Typography;

interface ClassOption {
  id: number;
  name: string;
  class_number: number | null;
}

interface StudentRow {
  id: number;
  code: string;
  full_name: string;
  card_balance: number;
  parent_name: string | null;
  parent: ParentDetails | null;
  // Memberships (every one a full member, CONTEXT.md) with what each owes.
  clubs: { name: string; outstanding: number }[];
  outstanding_amount: number;
}

type Filter = 'all' | 'outstanding' | 'paid' | 'none';

// Long club names are cut to fit; the full name shows on hover.
const MAX_CLUB_NAME = 35;
const shortName = (name: string) =>
  name.length > MAX_CLUB_NAME ? `${name.slice(0, MAX_CLUB_NAME)}…` : name;

const isMember = (s: StudentRow) => s.clubs.length > 0;

const matchesFilter = (s: StudentRow, filter: Filter) => {
  switch (filter) {
    case 'outstanding':
      return s.outstanding_amount > 0;
    case 'paid':
      return isMember(s) && s.outstanding_amount === 0;
    case 'none':
      return !isMember(s);
    default:
      return true;
  }
};

const columns: ColumnsType<StudentRow> = [
  { title: 'Код', dataIndex: 'code', width: 110 },
  { title: 'Сурагч', dataIndex: 'full_name' },
  {
    title: 'Эцэг эх',
    dataIndex: 'parent_name',
    render: (v, s) => <ParentLink name={v} parent={s.parent} />,
  },
  {
    title: 'Картын үлдэгдэл',
    dataIndex: 'card_balance',
    align: 'right',
    render: (v: number) => formatMNT(v),
  },
  {
    title: 'Дугуйлан',
    key: 'clubs',
    render: (_, s) =>
      isMember(s) ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          {s.clubs.map((c) => {
            const owing = c.outstanding > 0;
            // Tooltip: the full name when cut, and what is owed.
            const tip =
              c.name.length > MAX_CLUB_NAME || owing ? (
                <>
                  {c.name.length > MAX_CLUB_NAME && <div>{c.name}</div>}
                  {owing && <div>Төлөгдөөгүй дүн: {formatMNT(c.outstanding)}</div>}
                </>
              ) : undefined;
            // Paid up: plain grey; owing: orange with "-amount".
            return (
              <Tooltip key={c.name} title={tip}>
                <Tag color={owing ? 'orange' : undefined}>
                  {shortName(c.name)}
                  {owing && ` -${formatMNT(c.outstanding)}`}
                </Tag>
              </Tooltip>
            );
          })}
        </div>
      ) : (
        <Text type="secondary">Гишүүнчлэлгүй</Text>
      ),
  },
  {
    title: 'Төлөгдөөгүй',
    dataIndex: 'outstanding_amount',
    align: 'right',
    render: (v: number, s) => {
      if (!v) return '—';
      // Red when the card can't cover what's owed.
      return s.card_balance < v ? <Text type="danger">{formatMNT(v)}</Text> : formatMNT(v);
    },
  },
];

const ClassPayment = () => {
  const { runQuery } = useScreen();
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classId, setClassId] = useState<number>();
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');

  // Анги хурдан солиход хоцорсон хариу шинийг дарж бичихээс сэргийлнэ.
  const requestIdRef = useRef(0);

  useEffect(() => {
    setClassesLoading(true);
    runQuery<ClassOption>('class_options')
      .then((rows) => {
        setClasses(rows);
        if (rows.length === 1) setClassId(rows[0].id);
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setClassesLoading(false));
  }, [runQuery]);

  useEffect(() => {
    if (!classId) {
      setStudents([]);
      return;
    }
    const requestId = ++requestIdRef.current;
    setStudentsLoading(true);
    runQuery<StudentRow>('class_payment_students', { classId })
      .then((rows) => {
        if (requestId === requestIdRef.current) setStudents(rows);
      })
      .catch((e) => {
        if (requestId === requestIdRef.current) message.error(queryErrorMessage(e));
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setStudentsLoading(false);
      });
  }, [classId, runQuery]);

  const members = students.filter(isMember).length;
  const outstandingTotal = students.reduce((sum, s) => sum + s.outstanding_amount, 0);
  const visible = students.filter((s) => matchesFilter(s, filter));

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            placeholder="Анги сонгох"
            style={{ minWidth: 220 }}
            loading={classesLoading}
            value={classId}
            onChange={setClassId}
            showSearch
            optionFilterProp="label"
            options={classes.map((c) => ({ value: c.id, label: c.name }))}
          />
          {classId && !studentsLoading && (
            <Text type="secondary">
              {students.length} сурагч · {members} гишүүнтэй · {students.length - members}{' '}
              гишүүнчлэлгүй · Төлөгдөөгүй:{' '}
              <Text strong type={outstandingTotal > 0 ? 'danger' : undefined}>
                {formatMNT(outstandingTotal)}
              </Text>
            </Text>
          )}
        </div>
      </Card>

      {!classId && !classesLoading && (
        <Card>
          <Empty description="Анги сонгоно уу" />
        </Card>
      )}

      {classId && (
        <Card>
          <Segmented<Filter>
            style={{ marginBottom: 12 }}
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Бүгд' },
              { value: 'outstanding', label: 'Төлөгдөөгүй' },
              { value: 'paid', label: 'Бүгд төлөгдсөн' },
              { value: 'none', label: 'Гишүүнчлэлгүй' },
            ]}
          />
          {studentsLoading ? (
            <Spin />
          ) : (
            <Table
              rowKey="id"
              columns={columns}
              dataSource={visible}
              pagination={false}
              // Total of the rows shown (follows the filter), under Төлөгдөөгүй.
              summary={(rows) => {
                const total = rows.reduce((sum, s) => sum + s.outstanding_amount, 0);
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={columns.length - 1}>
                      <Text strong>Нийт төлөгдөөгүй</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={columns.length - 1} align="right">
                      <Text strong type={total > 0 ? 'danger' : undefined}>
                        {formatMNT(total)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                );
              }}
            />
          )}
        </Card>
      )}
    </LayoutWithMenu>
  );
};

export default ClassPayment;
